import { ReactElement, useState } from 'react';

import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import dayjs from 'dayjs';

import GratiPageWrapper from '../components/common/GratiPageWrapper';
import { useAuth } from '../contextProviders/AuthProvider';
import useEmailOutbox from '../dataHooks/useEmailOutbox';
import Email from '../types/Email';

function formatRecipients(to: Email['to']): string {
  return Array.isArray(to) ? to.join(', ') : to;
}

function formatCreateTime(createTime: Email['createTime']): string {
  return createTime && 'toDate' in createTime ? dayjs(createTime.toDate()).format('MMM D, h:mm:ss A') : '';
}

function OutboxEmail({ email }: { email: Email }): ReactElement {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Box
        sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Typography variant="titleMedium">{email.message.subject}</Typography>
        <Typography variant="bodySmall" sx={{ color: 'text.secondary' }}>
          {formatRecipients(email.to)} · {formatCreateTime(email.createTime)}
        </Typography>
      </Box>
      <Collapse in={isExpanded} unmountOnExit>
        {email.message.html ? (
          <Box sx={{ p: 2, pt: 0 }} dangerouslySetInnerHTML={{ __html: email.message.html }} />
        ) : (
          <Typography variant="bodyMedium" sx={{ p: 2, pt: 0, whiteSpace: 'pre-wrap' }}>
            {email.message.text}
          </Typography>
        )}
      </Collapse>
    </Box>
  );
}

/**
 * God menu, emulator only: the emails the app has queued. The emulator has no mail extension, so
 * nothing is delivered — this is where a developer reads them and follows their links.
 */
export default function DevOutboxPage(): ReactElement {
  const { isGod } = useAuth();
  const { emails, isEmailsLoading, emailsError } = useEmailOutbox();

  return (
    <GratiPageWrapper
      isAuthRequired={true}
      isNoAuthRequired={false}
      validateUser={() => isGod}
      isContentLoading={isEmailsLoading}
      contentError={emailsError}
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="headlineLarge" sx={{ mb: 2 }}>
          Dev outbox
        </Typography>
        {emails.length === 0 ? (
          <Typography variant="bodyMedium" sx={{ color: 'text.secondary' }}>
            No emails queued yet.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {emails.map((email) => (
              <OutboxEmail key={email.itemId} email={email} />
            ))}
          </Stack>
        )}
      </Box>
    </GratiPageWrapper>
  );
}
